/**
 * EstimatesContext.tsx
 * 
 * Backward-compatible context wrapper over standalone React Query hooks.
 * All standard data fetching and mutations are delegated to hooks/useEstimateQueries.ts.
 * 
 * Existing consumers can continue using `useEstimates()` without changes.
 */
import React, { createContext, useContext, ReactNode } from 'react';
import { nuevoId } from '../utils/nuevoId';
import { useIdioma } from '../i18n/idioma';
import { deletionBlock, MENSAJE_BLOQUEO } from '../utils/estimateDeletion';
import { useAuth } from './AuthContext';
import { useWorkspace } from './WorkspaceContext';
import { useSettings } from './SettingsContext';
import { makeDefaultProposalSettings } from '../services/proposalDefaults';
import {
    Estimate, PricebookItem, ServiceTemplate, EstimateStatus,
    PricingSnapshot, ProposalSettings, EstimateAdjustments
} from '../types';
import { getIndustryConfig } from '../data/industryConfig';
import {
    useEstimatesQuery, usePricebook, useServiceTemplates,
    useAddEstimate, useUpdateEstimate, useDeleteEstimate,
    useAddPricebookItem, useUpdatePricebookItem, useDeletePricebookItem,
    useAddServiceTemplate, useUpdateServiceTemplate
} from '../hooks/useEstimateQueries';

interface EstimatesContextType {
    estimates: Estimate[];
    /* Señales de carga y error para quien pinte estados vacíos.
       `estimates` viene con `= []` por defecto, así que sin esto un fetch en
       curso y un fetch caído son indistinguibles de «no hay ni una propuesta».
       El tablero de Seguimientos enseñaba «no hay propuestas» en los dos casos,
       que con mala señal en la obra es justo lo que pasa. */
    estimatesLoading: boolean;
    estimatesError: boolean;
    retryEstimates: () => void;
    pricebook: PricebookItem[];
    serviceTemplates: ServiceTemplate[];

    /**
     * Crea el estimado con UNA sola escritura.
     *
     * `adjustments` y `proposalSettings` se pueden mandar ya hechos —los usan
     * duplicar y el asistente de creación— y entonces sustituyen a los valores
     * por defecto EN EL MISMO INSERT. Antes se sembraban por defecto aquí y el
     * llamante los corregía con un `updateEstimate` justo después; ese segundo
     * PATCH llegaba a veces antes de que el INSERT hubiera confirmado, el
     * `.eq('id', …)` no encontraba fila, PostgREST devolvía 204 sin error y la
     * corrección entera se perdía en silencio. Ver `duplicateEstimate`.
     */
    addEstimate: (estimate: Omit<Estimate, 'id' | 'dateCreated' | 'lastUpdated' | 'status' | 'pricingSnapshot' | 'adjustments' | 'proposalSettings'> & {
        id?: string;
        adjustments?: EstimateAdjustments;
        proposalSettings?: ProposalSettings;
    }) => string;
    /** `silencioso` cuando el llamante ya enseña su propio diálogo de error. */
    updateEstimate: (id: string, updates: Partial<Estimate>, silencioso?: boolean) => Promise<void>;
    deleteEstimate: (id: string) => Promise<void>;
    updateEstimateStatus: (id: string, status: EstimateStatus) => Promise<void>;
    duplicateEstimate: (id: string) => void;
    updateEstimateProposalSettings: (id: string, settings: ProposalSettings) => void;

    addPricebookItem: (item: Omit<PricebookItem, 'id' | 'lastUpdated'>) => Promise<string>;
    updatePricebookItem: (id: string, updates: Partial<PricebookItem>) => Promise<void>;
    deletePricebookItem: (id: string) => Promise<void>;

    addServiceTemplate: (template: Omit<ServiceTemplate, 'id'>) => Promise<void>;
    updateServiceTemplate: (id: string, updates: Partial<ServiceTemplate>) => Promise<void>;
}

const EstimatesContext = createContext<EstimatesContextType | undefined>(undefined);

export const useEstimates = () => {
    const context = useContext(EstimatesContext);
    if (!context) {
        throw new Error('useEstimates must be used within an EstimatesProvider');
    }
    return context;
};

export const EstimatesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { t, idioma } = useIdioma();
    const { currentUser } = useAuth();
    const { settings, metrics } = useSettings();

    // --- QUERIES (delegated to standalone hooks) ---
    const {
        data: estimates = [] as Estimate[],
        isLoading: estimatesLoading,
        isError: estimatesError,
        refetch: refetchEstimates,
    } = useEstimatesQuery();
    const { data: pricebook = [] as PricebookItem[] } = usePricebook();
    const { data: serviceTemplates = [] as ServiceTemplate[] } = useServiceTemplates();

    // --- MUTATIONS (delegated to standalone hooks) ---
    const addEstimateMut = useAddEstimate();
    const updateEstimateMut = useUpdateEstimate();
    const deleteEstimateMut = useDeleteEstimate();
    const addPricebookItemMut = useAddPricebookItem();
    const updatePricebookItemMut = useUpdatePricebookItem();
    const deletePricebookItemMut = useDeletePricebookItem();
    const addServiceTemplateMut = useAddServiceTemplate();
    const updateServiceTemplateMut = useUpdateServiceTemplate();

    // --- ESTIMATES ---
    const addEstimate = (estimateData: Omit<Estimate, 'id' | 'dateCreated' | 'lastUpdated' | 'status' | 'pricingSnapshot' | 'adjustments' | 'proposalSettings'> & {
        id?: string;
        adjustments?: EstimateAdjustments;
        proposalSettings?: ProposalSettings;
    }) => {
        const snapshot: PricingSnapshot = {
            overheadPerManHour: metrics.overheadPerManHour,
            laborBurdenPercent: metrics.avgLaborBurdenPercent,
            targetMarginPercent: settings.pricing.targetMarginPercent,
            baseLaborCost: metrics.avgHourlyWage,
            targetHourlyRate: metrics.targetHourlyRate,
            materialMarkupPercent: settings.pricing.defaultMaterialMarkupPercent,
            breakEvenRate: metrics.breakEvenRate
        };

        const newId = estimateData.id || nuevoId();

        const newEstimate = {
            ...estimateData,
            dateCreated: new Date().toISOString(),
            lastUpdated: new Date().toISOString(),
            status: 'Draft' as EstimateStatus,
            pricingSnapshot: snapshot,
            // Lo que traiga el llamante manda; si no trae nada, valores por
            // defecto. Las dos ramas viajan en ESTE insert, nunca en un parche
            // posterior.
            adjustments: estimateData.adjustments ?? {
                tripCharge: false,
                emergencySurcharge: false,
                applyTax: false,
                taxLabor: settings.pricing.taxLaborDefault ?? false,
                taxRate: 8.25,
                minJobFeeApplied: false,
                discount: 0,
                discountType: 'flat' as const
            },
            proposalSettings: estimateData.proposalSettings ?? makeDefaultProposalSettings({
                title: `Proposal for ${estimateData.clientName}`,
                introMessage: "We appreciate the opportunity to provide this proposal. Please review the scope below.",
                settings,
                idioma,
            }),
            items: estimateData.items || [],
            createdBy: currentUser?.uid,
            createdByName: currentUser?.displayName || currentUser?.email || 'Unknown User'
        };

        addEstimateMut.mutate({ id: newId, data: newEstimate as Partial<Estimate> });
        return newId;
    };

    const updateEstimate = async (id: string, updates: Partial<Estimate>, silencioso?: boolean) => {
        await updateEstimateMut.mutateAsync({ id, updates, silencioso });
    };

    /**
     * Borra — si se puede. El candado vive AQUÍ y no en cada pantalla a
     * propósito: es la última puerta antes del DELETE, y no hay ninguna
     * restricción en la base que lo respalde (se comprobó contra producción:
     * ni una sola llave foránea apunta a `estimates`, así que Postgres deja
     * borrar cualquier fila sin rechistar). Una pantalla nueva que llame a
     * `deleteEstimate` queda protegida sin acordarse de nada.
     *
     * Lanza en vez de devolver `false`: quien borra ya tiene su `try/catch`
     * para el error de red, y así el bloqueo no se puede ignorar por descuido.
     */
    const deleteEstimate = async (id: string) => {
        const estimate = estimates.find(e => e.id === id);
        // Sin la fila no se puede juzgar. No se inventa un permiso: se niega.
        if (!estimate) throw new Error(t('estimados.borrar.noEncontrado'));

        const motivo = deletionBlock(estimate);
        if (motivo) throw new Error(t(MENSAJE_BLOQUEO[motivo]));

        await deleteEstimateMut.mutateAsync(id);
    };

    // `silencioso`: quien cambia el estado (aceptar / rechazar una propuesta) ya
    // abre su propio diálogo de error. Sin esto el usuario vería dos mensajes.
    const updateEstimateStatus = (id: string, status: EstimateStatus) => updateEstimate(id, { status }, true);

    const updateEstimateProposalSettings = (id: string, proposalSettings: ProposalSettings) => {
        // El `.catch` no se come nada: `useUpdateEstimate` ya restaura la caché y
        // avisa con un toast. Está para que la promesa no quede suelta — antes
        // esta llamada sin `await` producía un rechazo no capturado, invisible en
        // producción, y el usuario veía su cambio revertirse sin explicación.
        void updateEstimate(id, { proposalSettings }).catch(() => { /* ya avisado */ });
    };

    const duplicateEstimate = (id: string) => {
        const original = estimates.find(e => e.id === id);
        if (!original) return;

        // New item ids; zoneId is preserved so the copied zones still line up.
        const newItems = original.items.map(i => ({ ...i, id: nuevoId() }));

        // El «(Copy)» va en el TÍTULO de la propuesta, no en el nombre del
        // cliente. La copia conserva `clientId`, así que apunta al cliente de
        // verdad; renombrarlo hacía que ese nombre inventado —«Ana Torres
        // (Copy)»— se imprimiera en todo lo que ve el cliente, propuestas
        // enviadas incluidas. El título sí es del documento, se edita en el
        // constructor, y es lo que la lista de estimados enseña al lado del
        // cliente: la copia se distingue sin mentir sobre quién es.
        const proposalSettings = original.proposalSettings
            ? { ...original.proposalSettings, title: `${original.proposalSettings.title} (Copy)` }
            : undefined;

        // L-4: copia fiel, y en UNA sola escritura. `addEstimate` sella un
        // snapshot de precios nuevo, pero el resto del estado con sentido viaja
        // en el mismo INSERT: descuentos e impuestos, zonas, condiciones de
        // obra, la personalización de la propuesta y el oficio/alcance.
        //
        // Esto era un `updateEstimate(newId, {...})` justo después del INSERT, y
        // esa segunda escritura se perdía cuando llegaba primero: `.eq('id', …)`
        // no encontraba la fila todavía sin confirmar, PostgREST contestaba 204
        // con `error: null` y nadie se enteraba. Le pasó a una propuesta ya
        // ENVIADA, que quedó con un descuento de $910 evaporado y un total que
        // no cuadraba con sus propias partidas.
        const newId = addEstimate({
            clientId: original.clientId,
            clientName: original.clientName,
            items: newItems,
            total: original.total,
            validUntil: new Date(Date.now() + 604800000).toISOString(),
            adjustments: original.adjustments,
            zones: original.zones,
            siteConditions: original.siteConditions,
            proposalSettings,
            tradeId: original.tradeId,
            scopeCategoryId: original.scopeCategoryId,
            scopeLabel: original.scopeLabel,
            // La copia de una propuesta hecha a un prospecto sigue siendo suya.
            // Sin esto quedaría sin cliente Y sin prospecto: huérfana, y sin
            // nada que la convierta el día que el trato se gane.
            ...(original.sourceLeadId ? { sourceLeadId: original.sourceLeadId } : {}),
        });

        return newId;
    };

    /* AQUÍ VIVÍA `refreshEstimateRates`, y se borró en la ola 4 del
     * PLAN-pricing-model-v2. Se deja escrito porque el hueco no se explica solo:
     *
     * Estaba declarada en el tipo del contexto, definida, y expuesta en el
     * `value` — pero no la llamaba NADIE en toda la app (comprobado con grep:
     * las únicas tres apariciones eran esas tres). El botón «actualizar
     * tarifas» que tiene sentido para el usuario es el del panel del snapshot,
     * y ese llama a `refreshSnapshotFromCurrentSettings` de useEstimateForm.
     *
     * Y no era código muerto inofensivo, era una trampa cargada: quien la
     * hubiera llamado habría re-tarifado medio documento y roto lo que las olas
     * 0-3 acaban de establecer.
     *
     *   · Ponía la mano de obra a la tarifa objetivo del taller pero NO tocaba
     *     su `cost`: la fila quedaba con el precio de hoy y el costo de ayer, y
     *     el margen que enseñaba el constructor era mentira.
     *   · Se saltaba Equipment, Subcontractor y Fee — un permiso remitido a
     *     costo y una subcontrata se quedaban con la tarifa vieja mientras el
     *     resto se movía. Ninguna re-tarifación parcial es coherente.
     *   · Calculaba el precio del material a mano (`cost × (1 + markup/100)`) y
     *     derivaba de ahí un `marginPercent` que nadie había tecleado, en vez de
     *     usar `priceLineByType`. Es la aritmética duplicada del invariante
     *     A.6.1/A.6.3, y la que hacía que la misma partida se leyera distinta en
     *     tres pantallas.
     *   · Pisaba las filas fijadas a mano (`pricePinned`), que la ola 3 acaba de
     *     introducir: la decisión del contratista sobre una fila concreta
     *     desaparecía sin aviso.
     *
     * Si alguna vez hace falta «poner este estimado a los precios de hoy», el
     * sitio es useEstimateForm —donde ya viven la política, el pin y el factor
     * de margen neto—, no un atajo suelto en el contexto de datos.
     */

    // --- PRICEBOOK ---
    const addPricebookItem = async (itemData: Omit<PricebookItem, 'id' | 'lastUpdated'>): Promise<string> => {
        return await addPricebookItemMut.mutateAsync(itemData);
    };

    const updatePricebookItem = async (id: string, updates: Partial<PricebookItem>) => {
        await updatePricebookItemMut.mutateAsync({ id, updates });
    };

    const deletePricebookItem = async (id: string) => {
        await deletePricebookItemMut.mutateAsync(id);
    };

    const addServiceTemplate = async (templateData: Omit<ServiceTemplate, 'id'>) => {
        await addServiceTemplateMut.mutateAsync(templateData);
    };

    const updateServiceTemplate = async (id: string, updates: Partial<ServiceTemplate>) => {
        await updateServiceTemplateMut.mutateAsync({ id, updates });
    };

    return (
        <EstimatesContext.Provider value={{
            estimates, estimatesLoading, estimatesError,
            retryEstimates: () => { void refetchEstimates(); },
            pricebook, serviceTemplates,
            addEstimate, updateEstimate, deleteEstimate, updateEstimateStatus, duplicateEstimate, updateEstimateProposalSettings,
            addPricebookItem, updatePricebookItem, deletePricebookItem,
            addServiceTemplate, updateServiceTemplate
        }}>
            {children}
        </EstimatesContext.Provider>
    );
};
